import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    HttpResponse,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthController } from '@domain';
import { catchError, Observable, switchMap, tap, throwError } from 'rxjs';

@Injectable()
export class AccessTokenInterceptor implements HttpInterceptor {
    private _authController = inject(AuthController);
    private _router = inject(Router);

    /**
     * Rotas públicas (acessíveis sem login, ex.: preview de documento compartilhado).
     * Nessas páginas um 401/403 da API NÃO pode derrubar a sessão nem redirecionar para
     * o login: o visitante nunca esteve logado. O erro é apenas propagado (vira toast).
     */
    private static readonly PUBLIC_ROUTE_PATTERNS = [/\/document\/[^/]+\/preview/];

    /**
     * Rotas de sessão que NÃO podem disparar renovação ao falhar.
     *
     * `auth/refresh` é a própria renovação (tentar renovar a renovação daria loop infinito)
     * e `login`/`logout` não têm sessão para renovar: um 401 ali é credencial errada.
     */
    private static readonly SESSION_ENDPOINTS = [
        'auth/refresh',
        'auth/login',
        'auth/logout',
    ];

    private _isOnPublicRoute(): boolean {
        const url = this._router.url;
        return AccessTokenInterceptor.PUBLIC_ROUTE_PATTERNS.some(rx => rx.test(url));
    }

    private _isSessionEndpoint(url: string): boolean {
        return AccessTokenInterceptor.SESSION_ENDPOINTS.some(path => url.includes(path));
    }

    /**
     * Renova o token e REFAZ a requisição que tomou 401.
     *
     * Só o fracasso da RENOVAÇÃO desloga. Se a requisição refeita falhar por outro motivo
     * (403, 409, 500), o erro apenas sobe — a sessão continua válida.
     */
    private _retryWithRefresh(
        request: HttpRequest<unknown>,
        next: HttpHandler
    ): Observable<HttpEvent<unknown>> {
        return this._authController.refreshSession().pipe(
            catchError((refreshError: unknown) => {
                this._authController.logout();
                return throwError(() => refreshError);
            }),
            switchMap(token =>
                next.handle(request.clone({ headers: request.headers.set('Authorization', token) }))
            )
        );
    }

    /**
     * 401 com refresh token disponível vira uma tentativa de renovação, não um logout.
     * Fora desse caso, mantém o comportamento anterior (`_handleAuthError`).
     *
     * NÃO olha `_isOnPublicRoute()`: quem tem refresh token no cookie É um usuário logado,
     * e negar a renovação só porque a aba está no preview público derrubava justamente ele.
     * O cookie do access token dura 1 dia e o do refresh 60, então depois de um dia fora
     * TODA requisição autenticada sai sem `Authorization` e a API responde "Token não
     * informado" — que, no preview, virava toast em vez de renovação silenciosa. Visitante
     * de verdade não tem refresh token e continua sem renovar nada; quem impede o logout
     * dele é o `_handleAuthError`, que segue respeitando a rota pública.
     */
    private _shouldTryRefresh(error: HttpErrorResponse, request: HttpRequest<unknown>): boolean {
        return (
            error.status === 401 &&
            !this._isSessionEndpoint(request.url) &&
            !!this._authController.getRefreshTokenInCookie()
        );
    }

    /**
     * Desloga APENAS em 401 (sessão inválida: token ausente/expirado/inválido).
     *
     * Não desloga em 403 nem 409 — nenhum dos dois invalida a sessão:
     * - 403 = falta de permissão (RolesGuard) ou erro de fluxo de login (userInactive);
     *   ex.: um member tocando numa rota de admin não pode ser deslogado.
     * - 409 = conflito de negócio (e-mail/cliente duplicado) de um usuário logado.
     * Esses casos apenas propagam o erro (vira toast). Sessão inválida é sempre 401.
     * Em rotas públicas, nem o 401 desloga.
     */
    private _handleAuthError(error: HttpErrorResponse): void {
        const isSessionInvalid = error.status === 401;
        if (isSessionInvalid && !this._isOnPublicRoute()) {
            this._authController.logout();
        }
    }

    public intercept(
        request: HttpRequest<unknown>,
        next: HttpHandler
    ): Observable<HttpEvent<unknown>> {
        // Não adiciona Authorization header para requisições ao CDN ou proxy
        // Remove headers customizados que podem causar preflight requests
        const isCdnRequest =
            request.url.startsWith('/covers') ||
            request.url.includes('fover-docs-cdn-front.nyc3.digitaloceanspaces.com') ||
            request.url.includes('fover-docs-cdn-front.nyc3.cdn.digitaloceanspaces.com');

        if (isCdnRequest) {
            // Remove headers que podem causar preflight para garantir requisição simples
            // Isso permite que funcione com Access-Control-Allow-Origin: *
            const cleanRequest = request.clone({
                setHeaders: {},
                headers: request.headers
                    .delete('Authorization')
                    .delete('Accept')
                    .set('Accept', '*/*'), // Header simples que não causa preflight
            });
            return next.handle(cleanRequest);
        }

        const token = this._authController.getTokenInCookie();

        // If has token, add it to the request
        if (token) {
            const cloned = request.clone({
                headers: request.headers.set('Authorization', token),
            });

            return next.handle(cloned).pipe(
                tap(event => {
                    if (event instanceof HttpResponse) {
                        const authHeader = event.headers.get('Authorization');
                        if (authHeader) {
                            this._authController.setTokenInCookie(authHeader);
                        }
                    }
                }),
                catchError((error: HttpErrorResponse) => {
                    if (this._shouldTryRefresh(error, request)) {
                        return this._retryWithRefresh(request, next);
                    }

                    this._handleAuthError(error);

                    return throwError(() => error);
                })
            );
        }

        // If not has token, return the request
        return next.handle(request).pipe(
            tap(event => {
                if (event instanceof HttpResponse) {
                    const authHeader = event.headers.get('Authorization');
                    if (authHeader) {
                        this._authController.setTokenInCookie(authHeader);
                    }
                }
            }),
            catchError((error: HttpErrorResponse) => {
                // Sem access token no cookie (expirou) mas com refresh token vivo: renova e
                // refaz. É o caminho do usuário que volta ao app depois de 24h fora.
                if (this._shouldTryRefresh(error, request)) {
                    return this._retryWithRefresh(request, next);
                }

                this._handleAuthError(error);

                return throwError(() => error);
            })
        );
    }
}
